import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import QRCode from "react-qr-code";
import cx from "classnames";
import Header from "../Header/header";
import styles from "./summary.module.scss";
import { useEffect } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { toast } from "react-toastify";
import SummaryTutorialOverlay, {
  SummaryTutorialStep,
} from "./SummaryTutorialOverlay";

import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";

import SignatureCanvas from "react-signature-canvas";

/* ----------------------------- Types (minimal) ----------------------------- */
type Patient = {
  title?: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  mobile_phone?: string;
  home_phone?: string;
  address_line_1?: string;
  address_line_2?: string;
  nhs_number?: string;
};

type TreatmentPlan = {
  treatments?: { description: string }[];
};

type PlaylistItem = {
  id: string | number;
  title?: string;
  thumbnail?: string;
  status?: boolean;
};

type Playlist = {
  title?: string;
  status?: boolean;
  playlist_items?: PlaylistItem[];
};

type PlaylistActivityRow = {
  video_id: string | number;
  watch_percentage: number; // 0..1
  watch_status: "Not Watched" | "In Progress" | "Completed" | string;
  last_watched?: string | null;
  has_playback_issue?: boolean;
};

type SignatureMeta = {
  signed_type?: "manual" | "digital";
  signed_timestamp?: string | null;
};

type ClinicianSignatureMeta = {
  signed_timestamp?: string | null;
  signed_type?: "manual" | "digital" | string;
};

type SummaryDTO = {
  id: string | number;
  version_no: number;
  created_at: string;
  status: "Draft" | "Sent" | "Completed" | string;
  is_old_version?: boolean;
  link?: string;
  patient?: Patient;
  treatment_plan?: TreatmentPlan;
  clinician_table_id?: string | number;
  playlists?: Playlist[];
  playlist_activity?: PlaylistActivityRow[];
  patient_signature?: SignatureMeta;
  clinician_signature?: ClinicianSignatureMeta | null;
};

type VersionsRow = {
  consent_bundle_id: string;
  code: string;
  version: number;
};

type ComplaintStatus = "pending" | "resolved" | string;

type ComplaintRecord = {
  id: number | string;
  consent_bundle_id: number | string;
  complaint_body?: {
    issue?: string;
    details?: string;
    [key: string]: any;
  };
  status: ComplaintStatus;
};

/* ---------------------------- Utility functions ---------------------------- */
const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toDateString() : "-";
const percent = (x: number) =>
  `${(Math.min(Math.max(x, 0), 1) * 100).toFixed(0)}%`;

const escapePatientNoteHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatPatientNoteInlineMarkdown = (value: string) =>
  escapePatientNoteHtml(value)
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/__([^_]+)__/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/_([^_]+)_/g, "<em>$1</em>");

function patientNoteMarkdownToHtml(markdown: string) {
  const lines = markdown
    .replace(/\r\n?/g, "\n")
    .replace(/\n[ \t]*\n(?=[ \t]*(?:[-+*]\s+|\d+[.)]\s+))/g, "\n")
    .split("\n");
  const htmlParts: string[] = [];
  let openList: "ul" | "ol" | null = null;

  const closeList = () => {
    if (!openList) return;
    htmlParts.push(`</${openList}>`);
    openList = null;
  };

  const openListIfNeeded = (listType: "ul" | "ol") => {
    if (openList === listType) return;
    closeList();
    htmlParts.push(`<${listType}>`);
    openList = listType;
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) {
      closeList();
      return;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);

    if (headingMatch) {
      closeList();
      const headingLevel = headingMatch[1].length;
      htmlParts.push(
        `<h${headingLevel}>${formatPatientNoteInlineMarkdown(
          headingMatch[2],
        )}</h${headingLevel}>`,
      );
      return;
    }

    const unorderedListMatch = line.match(/^[-+*]\s+(.+)$/);

    if (unorderedListMatch) {
      openListIfNeeded("ul");
      htmlParts.push(
        `<li>${formatPatientNoteInlineMarkdown(
          unorderedListMatch[1],
        )}</li>`,
      );
      return;
    }

    const orderedListMatch = line.match(/^\d+[.)]\s+(.+)$/);

    if (orderedListMatch) {
      openListIfNeeded("ol");
      htmlParts.push(
        `<li>${formatPatientNoteInlineMarkdown(orderedListMatch[1])}</li>`,
      );
      return;
    }

    closeList();
    htmlParts.push(`<p>${formatPatientNoteInlineMarkdown(line)}</p>`);
  });

  closeList();
  return htmlParts.join("");
}

function patientNoteHtmlToPlainText(html: string) {
  const container = document.createElement("div");
  container.innerHTML = html;

  const walk = (node: ChildNode): string => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent || "";
    if (node.nodeType !== Node.ELEMENT_NODE) return "";

    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();
    const childText = Array.from(element.childNodes).map(walk).join("");

    if (tagName === "br") return "\n";

    if (tagName === "li") {
      const parentTag = element.parentElement?.tagName.toLowerCase();

      if (parentTag === "ol") {
        const siblings = Array.from(element.parentElement?.children || []);
        return `${siblings.indexOf(element) + 1}. ${childText.trim()}\n`;
      }

      return `- ${childText.trim()}\n`;
    }

    if (["ul", "ol"].includes(tagName)) {
      return `${childText.trim()}\n\n`;
    }

    if (
      /^h[1-6]$/.test(tagName) ||
      ["p", "div", "section", "blockquote"].includes(tagName)
    ) {
      const cleanText = childText.trim();
      return cleanText ? `${cleanText}\n\n` : "";
    }

    return childText;
  };

  return Array.from(container.childNodes)
    .map(walk)
    .join("")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function buildPatientNoteClipboardHtml(markdown: string) {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = patientNoteMarkdownToHtml(markdown);
  wrapper.style.fontFamily = "Arial, Helvetica, sans-serif";
  wrapper.style.fontSize = "14px";
  wrapper.style.lineHeight = "1.55";
  wrapper.style.color = "#111827";

  wrapper.querySelectorAll<HTMLElement>("h1, h2, h3, h4, h5, h6").forEach(
    (heading) => {
      heading.style.fontWeight = "700";
      heading.style.margin = "16px 0 8px";
      heading.style.lineHeight = "1.3";
    },
  );

  wrapper.querySelectorAll<HTMLElement>("h1").forEach((heading) => {
    heading.style.fontSize = "24px";
  });

  wrapper.querySelectorAll<HTMLElement>("h2, h3").forEach((heading) => {
    heading.style.fontSize = "20px";
  });

  wrapper.querySelectorAll<HTMLElement>("h4, h5, h6").forEach((heading) => {
    heading.style.fontSize = "16px";
  });

  wrapper.querySelectorAll<HTMLElement>("p").forEach((paragraph) => {
    paragraph.style.margin = "0 0 10px";
  });

  wrapper.querySelectorAll<HTMLElement>("ul, ol").forEach((list) => {
    list.style.margin = "0 0 12px";
    list.style.paddingLeft = "24px";
  });

  wrapper.querySelectorAll<HTMLElement>("li").forEach((item) => {
    item.style.margin = "0 0 6px";
  });

  return wrapper.outerHTML;
}

async function copyPatientNoteWithStyles(markdown: string) {
  const richHtml = buildPatientNoteClipboardHtml(markdown);
  const plainText = patientNoteHtmlToPlainText(richHtml);
  const ClipboardItemConstructor = (window as any).ClipboardItem;

  if (!plainText) {
    throw new Error("Patient note is empty");
  }

  if (navigator.clipboard?.write && ClipboardItemConstructor) {
    try {
      const clipboardItem = new ClipboardItemConstructor({
        "text/html": new Blob([richHtml], { type: "text/html" }),
        "text/plain": new Blob([plainText], { type: "text/plain" }),
      });

      await navigator.clipboard.write([clipboardItem]);
      return;
    } catch (error) {
      console.warn(
        "Rich clipboard copy failed. Trying the browser fallback.",
        error,
      );
    }
  }

  const copyContainer = document.createElement("div");
  copyContainer.innerHTML = richHtml;
  copyContainer.contentEditable = "true";
  copyContainer.style.position = "fixed";
  copyContainer.style.top = "0";
  copyContainer.style.left = "-9999px";
  copyContainer.setAttribute("aria-hidden", "true");

  document.body.appendChild(copyContainer);

  const selection = window.getSelection();
  const previousRanges = selection
    ? Array.from({ length: selection.rangeCount }, (_, index) =>
        selection.getRangeAt(index).cloneRange(),
      )
    : [];
  const range = document.createRange();

  range.selectNodeContents(copyContainer);
  selection?.removeAllRanges();
  selection?.addRange(range);

  try {
    const copied = document.execCommand("copy");

    if (!copied) {
      throw new Error("Copy command was blocked by the browser");
    }
  } finally {
    selection?.removeAllRanges();
    previousRanges.forEach((previousRange) =>
      selection?.addRange(previousRange),
    );
    document.body.removeChild(copyContainer);
  }
}

/* ------------------------------- Component -------------------------------- */
export default function Summary() {
  const { consentBundleId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<
    "CONSENT" | "WAITING" | "AUDIT" | "DOCS"
  >("CONSENT");
  const [showSummaryTutorial, setShowSummaryTutorial] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [versionList, setVersionList] = useState<VersionsRow[]>([]);
  const user: any = queryClient.getQueryData(["user"]);

  const summaryTutorialSteps = useMemo<SummaryTutorialStep[]>(
    () => [
      {
        id: "summary-overview",
        selector: ".summary-tutorial-topbar",
        title: "Consent summary overview",
        text: "This top section shows the current consent status, created date, version, consent bundle ID, treatment plan ID, and treatment summary.",
        placement: "bottom",
        beforeShow: () => setActiveTab("CONSENT"),
      },
      {
        id: "summary-download",
        selector: ".summary-tutorial-download",
        title: "Download the consent document",
        text: "Use this button to download the latest consent document for this bundle.",
        placement: "bottom",
        beforeShow: () => setActiveTab("CONSENT"),
      },
      {
        id: "summary-preview",
        selector: ".summary-tutorial-preview",
        title: "Preview before downloading",
        text: "Preview opens the latest generated consent PDF in a new tab so you can review it before saving or printing.",
        placement: "bottom",
        beforeShow: () => setActiveTab("CONSENT"),
      },
      {
        id: "summary-new-version",
        selector: ".summary-tutorial-new-version",
        title: "Update as a new version",
        text: "If the consent needs changes, create a new version instead of editing the sent version directly.",
        placement: "bottom",
        beforeShow: () => setActiveTab("CONSENT"),
      },
      {
        id: "summary-tabs",
        selector: ".summary-tutorial-tabs",
        title: "Summary sections",
        text: "Use these tabs to move between patient details, waiting room signing options, audit log, and documents/downloads.",
        placement: "bottom",
        beforeShow: () => setActiveTab("CONSENT"),
      },
      {
        id: "summary-secure-link",
        selector: ".summary-tutorial-secure-link",
        title: "Patient secure link",
        text: "This is the secure link the patient uses to open and complete the consent remotely.",
        placement: "bottom",
        beforeShow: () => setActiveTab("CONSENT"),
      },
      {
        id: "summary-patient-details",
        selector: ".summary-tutorial-patient-card",
        title: "Patient details",
        text: "Check the patient name, email, phone, address, and NHS number before sending or discussing the consent.",
        placement: "top",
        beforeShow: () => setActiveTab("CONSENT"),
      },
      {
        id: "summary-consent-status",
        selector: ".summary-tutorial-consent-status",
        title: "Consent status",
        text: "This card shows whether the consent is completed, sent for signing, or still unsigned. You can also print or mark manual signing from here when needed.",
        placement: "right",
        beforeShow: () => setActiveTab("CONSENT"),
      },
      {
        id: "summary-waiting-room-tab",
        selector: ".summary-tutorial-waiting-tab",
        title: "Waiting room signing options",
        text: "The waiting room tab contains manual signing, remote link sharing, and QR-code signing options.",
        placement: "bottom",
        beforeShow: () => setActiveTab("WAITING"),
      },
      {
        id: "summary-manual-signing",
        selector: ".summary-tutorial-manual-sign",
        title: "Manual signing at the practice",
        text: "Print a physical consent form for the patient to sign manually. The printed version does not include the digital audit log.",
        placement: "bottom",
        beforeShow: () => setActiveTab("WAITING"),
      },
      {
        id: "summary-remote-signing",
        selector: ".summary-tutorial-remote-sign",
        title: "Sign remotely",
        text: "Copy the secure link if you want to manually send it to the patient by email or text.",
        placement: "top",
        beforeShow: () => setActiveTab("WAITING"),
      },
      {
        id: "summary-qr-code",
        selector: ".summary-tutorial-qr-sign",
        title: "Sign in the waiting room",
        text: "Generate a QR code so the patient can scan it in the waiting room and open their consent documents.",
        placement: "top",
        beforeShow: () => setActiveTab("WAITING"),
      },
      {
        id: "summary-audit-tab",
        selector: ".summary-tutorial-audit-tab",
        title: "Audit log",
        text: "The audit log shows key patient-consent activity such as links being sent, opened, signed, or other recorded interactions.",
        placement: "bottom",
        beforeShow: () => setActiveTab("AUDIT"),
      },
      {
        id: "summary-audit-log",
        selector: ".summary-tutorial-audit-log",
        title: "Review audit events",
        text: "Use this timeline to review what happened and when. It helps you understand the patient journey and consent activity.",
        placement: "top",
        beforeShow: () => setActiveTab("AUDIT"),
      },
      {
        id: "summary-docs-tab",
        selector: ".summary-tutorial-docs-tab",
        title: "Documents and downloads",
        text: "This tab contains document versions, report downloads, and complaint-related record tools.",
        placement: "bottom",
        beforeShow: () => setActiveTab("DOCS"),
      },
      {
        id: "summary-documents",
        selector: ".summary-tutorial-docs-card",
        title: "Document versions",
        text: "Review available document versions here. If multiple versions exist, you can download the version you need.",
        placement: "top",
        beforeShow: () => setActiveTab("DOCS"),
      },
      {
        id: "summary-complaint",
        selector: ".summary-tutorial-complaint",
        title: "Patient complaint flag",
        text: "This area is reserved for complaint handling. When enabled, it can help flag the case and prompt full report download for records.",
        placement: "top",
        beforeShow: () => setActiveTab("DOCS"),
      },
      {
        id: "summary-dashboard-return",
        selector: ".summary-tutorial-dashboard-button",
        title: "Return to dashboard",
        text: "When you finish reviewing the summary, click here to go back to the dashboard.",
        placement: "left",
        beforeShow: () => setActiveTab("DOCS"),
      },
    ],
    [setActiveTab],
  );
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewId, setQuickViewId] = useState<string | number | null>(null);
  const [quickViewLoading, setQuickViewLoading] = useState(false);
  const [quickViewError, setQuickViewError] = useState("");
  const [interactionLog, setInteractionLog] = useState<any>({});

  const [copiedPatientNote, setCopiedPatientNote] = useState(false);
  const [copyingPatientNote, setCopyingPatientNote] = useState(false);
  const [patientNoteOpen, setPatientNoteOpen] = useState(false);
  const [patientNote, setPatientNote] = useState("");
  const [patientNoteLoading, setPatientNoteLoading] = useState(false);
  const [patientNoteError, setPatientNoteError] = useState("");
  const [showPrintConfirm, setShowPrintConfirm] = useState(false);
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [complaintIssue, setComplaintIssue] = useState("");
  const [complaintDetails, setComplaintDetails] = useState("");

  const [clinicianId, setClinicianId] = useState<string | number | "">("");
  const [clinicianSign, setClinicianSign] = useState(false);
  const [checkAccessRights, setCheckAccessRights] = useState<{
    is_owner: boolean;
    can_edit: boolean;
  }>({ is_owner: true, can_edit: true });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeInput, setActiveInput] = useState<"signature" | "name">(
    "signature",
  );
  const [userFullName, setUserFullName] = useState("");
  const [isSignatureCaptured, setIsSignatureCaptured] = useState(false);
  const [isSignatureApplied, setIsSignatureApplied] = useState(false);

  const mySignature = React.useRef<SignatureCanvas | null>(null);
  const isMobile = window.innerWidth <= 768;

  const summaryQuery = useQuery(
    ["summaryDetails", consentBundleId],
    async () => {
      const { data } = await axios.get<SummaryDTO>(
        `/consent_bundle/${consentBundleId}/activity_summary`,
      );

      if (data.status === "Draft" && (data as any)?.data?.treatment_plan_id) {
        navigate(`/${(data as any).data.treatment_plan_id}`);
      }

      return data;
    },
  );

  const data = summaryQuery.data;
  const patient = data?.patient;

  const complaintQuery = useQuery<ComplaintRecord | null>(
    ["complaintDetails", consentBundleId],
    async () => {
      try {
        const res = await axios.get<ComplaintRecord>(
          complaintApiUrl(`/complaint_management/complaint/${consentBundleId}`),
        );
        return res.data;
      } catch (error: any) {
        if (error?.response?.status === 404) return null;
        throw error;
      }
    },
    {
      enabled: activeTab === "DOCS" && !!consentBundleId,
      retry: false,
    },
  );

  const complaint = complaintQuery.data;

  /* ------------------------------- Apply My Signature ------------------------------ */

  const complaintApiUrl = (path: string) => {
    const baseUrl = process.env.REACT_APP_BASE_URL_V2?.replace(/\/$/, "") || "";
    return `${baseUrl}${path}`;
  };

  useEffect(() => {
    if (!data) return;

    if (
      data.clinician_table_id !== undefined &&
      data.clinician_table_id !== null
    ) {
      setClinicianId(data.clinician_table_id);
    }

    if (data.clinician_signature?.signed_timestamp) {
      setClinicianSign(true);
    } else {
      setClinicianSign(false);
    }
  }, [data]);

  // fetch access rights
  useEffect(() => {
    if (!consentBundleId) return;

    const fetchAccessRights = async () => {
      try {
        const res = await axios.get(
          `/consent_bundle/${consentBundleId}/check_access_rights`,
        );
        setCheckAccessRights(res.data);
      } catch (error) {
        console.error("Failed to fetch access rights:", error);
      }
    };

    fetchAccessRights();
  }, [consentBundleId]);

  const eraseSignature = () => {
    setIsSignatureCaptured(false);
    mySignature.current?.clear();
  };

  const markAsSignedByPractitioner = async () => {
    await axios.post(
      `/consent_bundle/${consentBundleId}/mark_as_signed_by_practitioner`,
      null,
      {
        headers: {
          Authorization: axios.defaults.headers.common.Authorization,
        },
      },
    );

    queryClient.invalidateQueries({ queryKey: ["summaryDetails"] });
  };

  const signLegacy = useMutation(
    async ({ image, name }: { image?: string; name?: string }) => {
      const token = axios.defaults.headers.common.Authorization;

      const formattedData = image ? { image } : { name };

      return axios.post(
        `/practitioner/${clinicianId}/save_signature`,
        formattedData,
        {
          headers: { Authorization: token },
        },
      );
    },
    {
      onSuccess: async () => {
        setIsModalOpen(false);
        setIsSignatureApplied(true);
        await markAsSignedByPractitioner();
      },
    },
  );

  const handleModalOpen = async () => {
    try {
      if (!clinicianId) {
        console.error("Missing clinicianId (clinician_table_id)");
        return;
      }

      const res = await axios.get(
        `/practitioner/${clinicianId}/check_if_signature_exists`,
      );
      const signatureExists = res.data;

      if (!signatureExists) {
        // open modal to capture signature
        setActiveInput("signature");
        setIsSignatureCaptured(false);
        setIsModalOpen(true);
        return;
      }

      // signature exists -> just apply it
      await markAsSignedByPractitioner();
      setIsSignatureApplied(true);
    } catch (error) {
      console.error("Error checking clinician signature:", error);
    }
  };

  const handleSignatureSave = () => {
    if (!mySignature.current) return;
    const signatureData = mySignature.current.toDataURL();
    signLegacy.mutate({ image: signatureData });
  };

  const handleNameSave = () => {
    if (!userFullName) return;
    signLegacy.mutate({ name: userFullName });
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setActiveInput("signature");
    setUserFullName("");
    eraseSignature();
  };
  /* ------------------------------- Apply My Signature ------------------------------ */

  /* ------------------------------- Data load ------------------------------ */
  // const summaryQuery = useQuery(["summaryDetails", consentBundleId], async () => {
  //   const { data } = await axios.get<SummaryDTO>(`/consent_bundle/${consentBundleId}/activity_summary`);
  //   // Keep your existing behavior
  //   if (data.status === "Draft" && (data as any)?.data?.treatment_plan_id) {
  //     navigate(`/${(data as any).data.treatment_plan_id}`);
  //   }
  //   return data;
  // });

  // const data = summaryQuery.data;
  // const patient = data?.patient;

  /* ------------------------------ Mutations ------------------------------ */
  const manuallySign = useMutation(
    async (id: string | number) =>
      axios.put(`/consent_bundle/${id}/mark_consent_bundle_as_signed`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["summaryDetails"] });
      },
    },
  );

  const openConsentBundle = async (bundleId: string | number) => {
  try {
    const res = await axios.get(`/consent_bundle/${bundleId}`);
    const bundle = res.data?.consent_bundle ?? res.data;

    console.log("Consent bundle:", bundle);

    if (!bundle?.status) {
      toast.error("No status found for this consent bundle");
      return;
    }

    const status = String(bundle.status).trim().toLowerCase();

    if (["completed", "signed", "expired"].includes(status)) {
      navigate(`/summary-new/${bundleId}`);
      return;
    }

    if (status === "draft") {
      sessionStorage.setItem(
        "activeTreatmentData",
        JSON.stringify(bundle)
      );

      navigate(
        `/new-consentBundle?step=1&consentBundleId=${bundleId}`
      );
      return;
    }

    if (
      status.includes("sent") ||
      status.includes("unsigned") ||
      status.includes("sign")
    ) {
      navigate(`/summary-new/${bundleId}`);
      return;
    }

    toast.error(`Cannot open — this consent is ${bundle.status}`);
  } catch (err: any) {
    console.error("Failed to fetch consent bundle:", err);

    toast.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Error loading consent bundle"
    );
  }
};

  const createNewVersion = useMutation(
    async (id: string | number) =>
      axios.post(`/consent_bundle/${id}/create_consent_bundle_new_version`),
    {
      onSuccess: async (res) => {
        const newId =
          res.data?.consent_bundle_id ??
          res.data?.id ??
          res.data?.data?.id ??
          res.data?.data?.consent_bundle_id ??
          res.data;

        if (!newId) {
          toast.error(
            "Could not create new consent bundle version (missing new id).",
          );
          return;
        }

        await openConsentBundle(newId);
      },
      onError: (err) => {
        console.error("Failed to Edit document:", err);
        toast.error("Failed to Edit document");
      },
    },
  );

  const resetComplaintForm = () => {
    setComplaintIssue("");
    setComplaintDetails("");
  };

  const createComplaint = useMutation(
    async () =>
      axios.post(complaintApiUrl(`/complaint_management/${consentBundleId}`), {
        complaint_body: {
          issue: complaintIssue.trim(),
          details: complaintDetails.trim(),
        },
      }),
    {
      onSuccess: async () => {
        toast.success("Patient complaint flagged successfully");
        setShowComplaintModal(false);
        resetComplaintForm();
        await queryClient.invalidateQueries({
          queryKey: ["complaintDetails", consentBundleId],
        });
      },
      onError: (err) => {
        console.error("Failed to create complaint:", err);
        toast.error("Failed to flag patient complaint");
      },
    },
  );

  const updateComplaintStatus = useMutation(
    async (status: "pending" | "resolved") =>
      axios.patch(
        complaintApiUrl(`/complaint_management/status/${consentBundleId}`),
        {
          status,
        },
      ),
    {
      onSuccess: async () => {
        toast.success("Complaint status updated");
        await queryClient.invalidateQueries({
          queryKey: ["complaintDetails", consentBundleId],
        });
      },
      onError: (err) => {
        console.error("Failed to update complaint status:", err);
        toast.error("Failed to update complaint status");
      },
    },
  );

  const handleCreateComplaint = () => {
    if (!complaintIssue.trim()) {
      toast.error("Please enter the complaint issue");
      return;
    }

    if (!complaintDetails.trim()) {
      toast.error("Please enter the complaint details");
      return;
    }

    createComplaint.mutate();
  };

  const handleCloseComplaintModal = () => {
    if (createComplaint.isLoading) return;
    setShowComplaintModal(false);
    resetComplaintForm();
  };

  /* --------------------------- Derived bits --------------------------- */
  const treatmentTitle = useMemo(() => {
    const list = data?.treatment_plan?.treatments || [];
    if (!list.length) return "-";
    return list.map((t) => t.description).join(", ");
  }, [data]);

  const createdOn = formatDate(data?.created_at);
  const secureConsentLink =
    data?.link || `${process.env.REACT_APP_PATIENT_URL}/${consentBundleId ?? ""}`;
  const hasMultipleVersions = (data?.version_no ?? 1) > 1;

  // activity map (kept in case you show video status elsewhere)
  const activityMap = useMemo(() => {
    const map = new Map<string | number, PlaylistActivityRow>();
    (data?.playlist_activity || []).forEach((row) =>
      map.set(row.video_id, row),
    );
    return map;
  }, [data]);

  const visiblePlaylists = (data?.playlists || []).filter((p) => p.status);
  const anyVideoIssue = useMemo(
    () => (data?.playlist_activity || []).some((r) => r.has_playback_issue),
    [data],
  );

  /* --------------------------- Action handlers --------------------------- */
  const downloadLatest = async () => {
    if (!data) return;
    setIsDownloading(true);
    try {
      const completed = data.status === "Completed";
      const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${consentBundleId}/${completed ? "generate_pdf" : "generate_offline_pdf"}`;

      const resp = await axios.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);

      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", getPdfName(data));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } finally {
      setIsDownloading(false);
    }
  };

  const previewLatest = async () => {
    if (!data) return;
    setIsDownloading(true);
    try {
      const completed = data.status === "Completed";
      const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${consentBundleId}/${completed ? "generate_pdf" : "generate_offline_pdf"}`;

      const resp = await axios.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);
      window.open(href, "_blank", "noopener,noreferrer");
      // don’t revoke immediately; allow browser to load it
      setTimeout(() => URL.revokeObjectURL(href), 30_000);
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadFullReport = async () => {
    if (!data) return;
    setIsDownloading(true);
    try {
      const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${consentBundleId}/download_full_consent_bundle_report`;
      const resp = await axios.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);

      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", getPdfName(data));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadVersion = async (bundleId: string) => {
    setIsDownloading(true);
    try {
      const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${bundleId}/generate_pdf`;
      const resp = await axios.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);

      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", getPdfName(data || ({} as any)));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleVersionClick = async () => {
    if (!hasMultipleVersions) return;

    if (showDropdown) {
      setShowDropdown(false);
      return;
    }

    try {
      const resp = await axios.get<VersionsRow[]>(
        `/consent_bundle/${consentBundleId}/get_consent_bundle_versions`,
      );
      setVersionList(resp.data || []);
      setShowDropdown(true);
    } catch (err) {
      console.error("Error fetching versions:", err);
      setVersionList([]);
      setShowDropdown(true);
    }
  };

  const handleSelectVersion = async (bundleId: string | number) => {
    setShowDropdown(false);
    navigate(`/summary/${bundleId}`);
  };

  const copyRemoteLink = async () => {
    if (!data) return;

    try {
      await navigator.clipboard.writeText(secureConsentLink);
      toast.success("Link copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy link. Please try again.");
    }
  };

  const sendReminder = async () => {
    if (!consentBundleId) return;

    setIsSendingReminder(true);
    try {
      await axios.post(`/consent_bundle/${consentBundleId}/send_reminder`);
      toast.success("Reminder sent to patient");
    } catch (error) {
      console.error("Failed to send reminder:", error);
      toast.error("Failed to send reminder");
    } finally {
      setIsSendingReminder(false);
    }
  };

  const getPatientNoteFromBundle = (bundle: any) => {
    const possiblePatientNotes = [
      bundle?.notes,
      bundle?.patient_note,
      bundle?.patientNote,
      bundle?.data?.notes,
      bundle?.data?.patient_note,
      bundle?.data?.patientNote,
      bundle?.consent_bundle?.notes,
      bundle?.consent_bundle?.patient_note,
      bundle?.consent_bundle?.patientNote,
    ];

    const patientNote = possiblePatientNotes.find(
      (value) => typeof value === "string" && value.trim(),
    );

    return typeof patientNote === "string" ? patientNote.trim() : "";
  };

  const fetchPatientNote = async () => {
    if (!consentBundleId) {
      throw new Error("Consent bundle ID is not available.");
    }

    const res = await axios.get(`/consent_bundle/${consentBundleId}`);
    const bundle = res.data?.consent_bundle ?? res.data?.data ?? res.data;
    const note = getPatientNoteFromBundle(bundle);

    if (!note) {
      throw new Error("No patient note is available for this consent bundle.");
    }

    return note;
  };

  const viewPatientNote = async () => {
    if (!consentBundleId) return;

    setPatientNoteOpen(true);
    setPatientNote("");
    setPatientNoteError("");
    setPatientNoteLoading(true);

    try {
      const note = await fetchPatientNote();
      setPatientNote(note);
    } catch (error) {
      console.error("Failed to load patient note:", error);
      setPatientNoteError(
        error instanceof Error
          ? error.message
          : "Failed to load the patient note. Please try again.",
      );
    } finally {
      setPatientNoteLoading(false);
    }
  };

  const copyPatientNote = async (fetchLatest = false) => {
    if (!consentBundleId) return;

    setCopyingPatientNote(true);

    try {
      const note =
        fetchLatest || !patientNote.trim()
          ? await fetchPatientNote()
          : patientNote.trim();

      setPatientNote(note);
      await copyPatientNoteWithStyles(note);

      setCopiedPatientNote(true);
      toast.success("Patient note copied to clipboard");

      setTimeout(() => {
        setCopiedPatientNote(false);
      }, 1800);
    } catch (error) {
      console.error("Failed to copy patient note:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to copy patient note",
      );
    } finally {
      setCopyingPatientNote(false);
    }
  };

  const closePatientNote = () => {
    setPatientNoteOpen(false);
    setCopiedPatientNote(false);
  };

  const loadAuditLog = async (id: string | number) => {
    setQuickViewLoading(true);
    setQuickViewError("");
    setInteractionLog({});

    try {
      const res = await axios.get(`/consent_bundle/${id}/interaction_log`);

      // API returns an object keyed by date
      setInteractionLog(res.data || {});
    } catch (err) {
      console.error(err);
      setQuickViewError("Failed to load audit log");
    } finally {
      setQuickViewLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "AUDIT" && consentBundleId) {
      loadAuditLog(consentBundleId);
    }
  }, [activeTab, consentBundleId]);

  const versionStatusLabel = (v: VersionsRow, latestVersion: number) => {
    if (v.version === latestVersion) return "Current";
    if (v.version < latestVersion) return "Archived";
    return "Signed";
  };

  useEffect(() => {
    if (activeTab === "DOCS" && consentBundleId) {
      handleVersionClick();
    }
  }, [activeTab]);

  const printOfflineConsent = async () => {
    if (!data) return;

    setIsDownloading(true);
    try {
      const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${consentBundleId}/generate_offline_pdf`;

      const resp = await axios.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);

      // Open print dialog
      const win = window.open(href, "_blank");
      win?.addEventListener("load", () => {
        win.print();
      });

      setTimeout(() => URL.revokeObjectURL(href), 30_000);
    } finally {
      setIsDownloading(false);
    }
  };

  // Show the print/download button ONLY after manual signing is done
  const isManualSigned = data?.patient_signature?.signed_type === "manual";

  const patientFullName = patient
    ? `${patient.title ?? ""} ${patient.first_name ?? ""} ${patient.last_name ?? ""}`.trim() ||
      "-"
    : "-";

  const patientAddress = patient
    ? `${patient.address_line_1 || ""}${
        patient.address_line_2 ? `, ${patient.address_line_2}` : ""
      }`.trim() || "-"
    : "-";

  const patientSignatureDone = Boolean(data?.patient_signature?.signed_timestamp);
  const clinicianSignatureDone = Boolean(
    data?.clinician_signature?.signed_timestamp || clinicianSign,
  );


  const handleMarkManualSigned = async () => {
    if (!data) return;
    try {
      await manuallySign.mutateAsync(data.id);
      setShowPrintConfirm(false);
      await queryClient.invalidateQueries({ queryKey: ["summaryDetails"] });
    } catch (err) {
      console.error("Mark manual signed failed", err);
    }
  };

  /* -------------------------------- Render -------------------------------- */
  return (
    <>
      <Header />

      <div className={styles.page}>
        <div className={styles.headerBlock}>
          <div className={styles.headerText}>
            <h1 className={styles.pageTitle}>Summary</h1>
            <p className={styles.pageSubtitle}>
              Here’s a summary of your patient’s consent journey
            </p>
          </div>

          <button
            type="button"
            className={styles.tutorialInfoButton}
            onClick={() => setShowSummaryTutorial(true)}
            title="Show tutorials"
            aria-label="Show tutorials"
          >
            i
          </button>
        </div>

        {/* Top orange bordered summary bar (like screenshot) */}
        <div className={cx(styles.topBar, "summary-tutorial-topbar")}>
          <div className={styles.topBarRow}>
            <span
              className={cx(styles.statusPillTop, {
                [styles.statusCompleted]: data?.status === "Completed",
                [styles.statusOther]: data?.status !== "Completed",
              })}
            >
              {data?.status || "—"}
            </span>

            <span className={styles.sep} />

            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Created:</span>
              <span className={styles.metaValue}>{createdOn}</span>
            </div>

            <span className={styles.sep} />

            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Version:</span>
              <span className={styles.metaValue}>
                {data?.version_no ?? "-"}
              </span>
            </div>

            <span className={styles.sep} />

            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Consent bundle ID:</span>
              <span className={styles.metaValue}>{data?.id ?? "-"}</span>
            </div>

            <span className={styles.sep} />

            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Treatment plan ID:</span>
              <span className={styles.metaValue}>
                {data?.treatment_plan_id ?? "-"}
              </span>
            </div>

            <span className={styles.sep} />

            <div className={styles.metaItemTruncate} title={treatmentTitle}>
              <span className={styles.metaValue}>{treatmentTitle}</span>
            </div>
          </div>

          <div className={styles.topBarActions}>
            <button
              className={cx(styles.iconBtn, "summary-tutorial-download")}
              onClick={downloadLatest}
              disabled={isDownloading || summaryQuery.isLoading}
              title="Download latest consent PDF"
            >
              <DownloadIcon />
              <span>Download PDF</span>
            </button>

            <button
              className={cx(styles.outlineBtn, "summary-tutorial-preview")}
              onClick={previewLatest}
              disabled={isDownloading || summaryQuery.isLoading}
            >
              <VisibilityOutlinedIcon fontSize="small" />
              Preview PDF
            </button>


 <div
              role="group"
              aria-label="Patient note actions"
              style={{ display: "inline-flex", alignItems: "stretch" }}
            >
              <button
                type="button"
                className={styles.outlineBtn}
                onClick={viewPatientNote}
                disabled={
                  !data || patientNoteLoading || copyingPatientNote
                }
                title="Preview patient note"
                style={{
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                }}
              >
                <VisibilityOutlinedIcon fontSize="small" />
                {patientNoteLoading
                  ? "Loading patient note..."
                  : "Preview patient note"}
              </button>

              <button
                type="button"
                className={styles.outlineBtn}
                onClick={() => copyPatientNote(true)}
                disabled={
                  !data || patientNoteLoading || copyingPatientNote
                }
                title={
                  copiedPatientNote
                    ? "Patient note copied"
                    : "Copy patient note without opening"
                }
                aria-label={
                  copiedPatientNote
                    ? "Patient note copied"
                    : "Copy patient note without opening"
                }
                style={{
                  minWidth: 42,
                  paddingLeft: 10,
                  paddingRight: 10,
                  marginLeft: -1,
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                }}
              >
                {copiedPatientNote ? (
                  <CheckOutlinedIcon fontSize="small" />
                ) : (
                  <ContentCopyOutlinedIcon fontSize="small" />
                )}
              </button>
            </div>

            <button
              className={cx(styles.outlineBtn, "summary-tutorial-new-version")}
              onClick={() => data && createNewVersion.mutate(data.id)}
              disabled={
                !data || createNewVersion.isLoading || !!data?.is_old_version
              }
              title={
                data?.is_old_version
                  ? "Old versions can’t be edited"
                  : "Edit as new version"
              }
            >
              <DescriptionOutlinedIcon fontSize="small" />
              Edit document
            </button>

           
          </div>
        </div>

        {/* Tabs row */}
        <div className={cx(styles.tabs, "summary-tutorial-tabs")}>
          <button
            className={cx(styles.tab, {
              [styles.tabActive]: activeTab === "CONSENT",
            })}
            onClick={() => setActiveTab("CONSENT")}
          >
            Consent &amp; Patient
          </button>
          <button
            className={cx(styles.tab, "summary-tutorial-waiting-tab", {
              [styles.tabActive]: activeTab === "WAITING",
            })}
            onClick={() => setActiveTab("WAITING")}
          >
            Waiting Room
          </button>
          <button
            className={cx(styles.tab, "summary-tutorial-audit-tab", {
              [styles.tabActive]: activeTab === "AUDIT",
            })}
            onClick={() => setActiveTab("AUDIT")}
          >
            Audit Log
          </button>
          <button
            className={cx(styles.tab, "summary-tutorial-docs-tab", {
              [styles.tabActive]: activeTab === "DOCS",
            })}
            onClick={() => setActiveTab("DOCS")}
          >
            Documents &amp; Downloads
          </button>
        </div>

        {showPrintConfirm && (
          <div
            className={styles.modalBackdrop}
            onClick={() => setShowPrintConfirm(false)}
          >
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <h3 className={styles.modalTitle}>Print Consent Form</h3>

              <p className={styles.modalText}>
                I declare that I will present this to the patient to sign
                manually and I understand there will be no digital audit log
                attached to this version.
              </p>

              <div className={styles.modalActions}>
                <button
                  className={styles.outlineBtn}
                  onClick={() => setShowPrintConfirm(false)}
                >
                  Cancel
                </button>

                <button
                  className={styles.primaryBtn}
                  disabled={manuallySign.isLoading || isDownloading}
                  onClick={handleMarkManualSigned}
                >
                  I Understand, Mark as signed
                </button>
              </div>
            </div>
          </div>
        )}

      
        {activeTab === "CONSENT" && (
          <>
            <div className={styles.sectionHeader}>
              {/* <div>
                <p className={styles.sectionEyebrow}>Consent &amp; patient</p>
                <h2 className={styles.sectionTitle}>Check the important details</h2>
                <p className={styles.sectionSubtitle}>
                  Everything here is read-only and shown in plain language so the
                  team can confirm the consent quickly.
                </p>
              </div> */}
            </div>

            <div className={styles.waitingRoomList}>
              <section
                className={cx(
                  styles.waitingRoomCard,
                  styles.featureCard,
                  "summary-tutorial-secure-link",
                )}
              >
                <div className={styles.waitingRoomIconBox}>
                  <LinkOutlinedIcon fontSize="small" />
                </div>

                <div className={styles.waitingRoomText}>
                  <h2 className={styles.waitingRoomTitle}>Patient secure link</h2>
                  <p className={styles.waitingRoomSubtitle}>
                    Share this only with the patient when they need remote access.
                  </p>
                  <p className={styles.waitingRoomLink} title={secureConsentLink}>s
                    {secureConsentLink}
                  </p>
                </div>

                <div className={styles.waitingRoomActions}>
                  <button
                    type="button"
                    className={cx(styles.waitingRoomBtn, styles.waitingRoomBtnPrimary)}
                    onClick={copyRemoteLink}
                    disabled={!data}
                  >
                    <ContentCopyOutlinedIcon fontSize="small" />
                    <span>Copy link</span>
                  </button>
                </div>
              </section>
            </div>

            <section
              className={cx(
                styles.simplePanel,
                "summary-tutorial-patient-card",
              )}
            >
              <div className={styles.panelHeader}>
                <div className={styles.panelIcon}>
                  <PersonOutlineOutlinedIcon fontSize="small" />
                </div>
                <div>
                  <h2 className={styles.panelTitle}>Patient details</h2>
                  <p className={styles.panelSubtitle}>
                    Confirm the patient identity before sharing, printing, or signing.
                  </p>
                </div>
              </div>

              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Name</span>
                  <strong className={styles.detailValue}>{patientFullName}</strong>
                </div>

                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Email</span>
                  <strong className={styles.detailValue}>
                    {patient?.email_address || "-"}
                  </strong>
                </div>

                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Mobile</span>
                  <strong className={styles.detailValue}>
                    {patient?.mobile_phone || "-"}
                  </strong>
                </div>

                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Phone</span>
                  <strong className={styles.detailValue}>
                    {patient?.home_phone || "-"}
                  </strong>
                </div>

                <div className={cx(styles.detailItem, styles.detailWide)}>
                  <span className={styles.detailLabel}>Address</span>
                  <strong className={styles.detailValue}>{patientAddress}</strong>
                </div>

                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>NHS number</span>
                  <strong className={styles.detailValue}>
                    {patient?.nhs_number || "-"}
                  </strong>
                </div>
              </div>
            </section>

            <section
              className={cx(
                styles.simplePanel,
                styles.compactPanel,
                "summary-tutorial-consent-status",
              )}
            >
              <div className={styles.panelHeader}>
                <div className={styles.panelIcon}>
                  <FactCheckOutlinedIcon fontSize="small" />
                </div>
                <div>
                  <h2 className={styles.panelTitle}>Consent status</h2>
                  <p className={styles.panelSubtitle}>
                    See the signing state and use the next available action.
                  </p>
                </div>
              </div>

              <div className={styles.simpleActionGrid}>
                <div className={styles.simpleActionCard}>
                  <div>
                    <span className={styles.detailLabel}>Current status</span>
                    <h3 className={styles.simpleActionTitle}>
                      {data?.status || "—"}
                    </h3>
                    <p className={styles.simpleActionText}>
                      This is the current state of the consent bundle.
                    </p>
                  </div>

                  <span
                    className={cx(styles.smallPill, {
                      [styles.pillGreen]: data?.status === "Completed",
                      [styles.pillOrange]: data?.status !== "Completed",
                    })}
                  >
                    {data?.status === "Completed" ? "COMPLETED" : "OPEN"}
                  </span>
                </div>

                <div className={styles.simpleActionCard}>
                  <div>
                    <span className={styles.detailLabel}>Patient signature</span>
                    <h3 className={styles.simpleActionTitle}>
                      {patientSignatureDone || data?.status === "Completed"
                        ? "Signed"
                        : "Not signed"}
                    </h3>
                    <p className={styles.simpleActionText}>
                      Use manual signing only when the patient signs on paper.
                    </p>
                  </div>

                  <div className={styles.inlineActionGroup}>
                    <button
                      type="button"
                      className={cx(styles.waitingRoomBtn, styles.waitingRoomBtnLight)}
                      onClick={printOfflineConsent}
                      disabled={!data || isDownloading || summaryQuery.isLoading}
                    >
                      <PrintOutlinedIcon fontSize="small" />
                      <span>{isDownloading ? "Preparing..." : "Print"}</span>
                    </button>

                    <button
                      type="button"
                      className={cx(styles.waitingRoomBtn, styles.waitingRoomBtnSuccess)}
                      onClick={() => setShowPrintConfirm(true)}
                      disabled={!data || manuallySign.isLoading || isManualSigned}
                      title={
                        isManualSigned
                          ? "Already marked as signed manually"
                          : "Mark this consent as signed manually"
                      }
                    >
                      <CheckOutlinedIcon fontSize="small" />
                      <span>
                        {manuallySign.isLoading
                          ? "Saving..."
                          : isManualSigned
                            ? "Signed"
                            : "Mark signed"}
                      </span>
                    </button>
                  </div>
                </div>

                <div className={styles.simpleActionCard}>
                  <div>
                    <span className={styles.detailLabel}>Clinician signature</span>
                    <h3 className={styles.simpleActionTitle}>
                      {clinicianSignatureDone ? "Applied" : "Not signed"}
                    </h3>
                    <p className={styles.simpleActionText}>
                      Apply your saved signature or add one when needed.
                    </p>
                  </div>

                  <button
                    type="button"
                    className={cx(styles.waitingRoomBtn, styles.waitingRoomBtnPrimary)}
                    onClick={handleModalOpen}
                    disabled={!data || !clinicianId || clinicianSignatureDone}
                  >
                    <CheckOutlinedIcon fontSize="small" />
                    <span>{clinicianSignatureDone ? "Applied" : "Apply signature"}</span>
                  </button>
                </div>
              </div>
            </section>

            <div className={styles.footer}>
              <button
                className={cx(
                  styles.outlineBtn,
                  "summary-tutorial-dashboard-button",
                )}
                onClick={() => navigate("/dashboard")}
              >
                Go to dashboard
              </button>
            </div>
          </>
        )}

        {activeTab === "WAITING" && (
          <>
            <div className={styles.sectionHeader}>
              <div>
                <p className={styles.sectionEyebrow}>Waiting room</p>
                <h2 className={styles.sectionTitle}>Choose how the patient signs</h2>
                <p className={styles.sectionSubtitle}>
                  Pick one simple action: print, send the link, or show a QR code.
                </p>
              </div>
            </div>

            <div className={styles.waitingRoomList}>
              <section
                className={cx(
                  styles.waitingRoomCard,
                  "summary-tutorial-manual-sign",
                )}
              >
                <div className={styles.waitingRoomIconBox}>
                  <PrintOutlinedIcon fontSize="small" />
                </div>

                <div className={styles.waitingRoomText}>
                  <h2 className={styles.waitingRoomTitle}>
                    Print for signature
                  </h2>
                  <p className={styles.waitingRoomSubtitle}>
                    Patient signs on paper in the practice.
                  </p>
                </div>

                <div className={styles.waitingRoomActions}>
                  <button
                    type="button"
                    className={cx(styles.waitingRoomBtn, styles.waitingRoomBtnLight)}
                    onClick={printOfflineConsent}
                    disabled={!data || isDownloading}
                  >
                    <PrintOutlinedIcon fontSize="small" />
                    <span>{isDownloading ? "Preparing..." : "Print"}</span>
                  </button>

                  <button
                    type="button"
                    className={cx(styles.waitingRoomBtn, styles.waitingRoomBtnSuccess)}
                    onClick={() => setShowPrintConfirm(true)}
                    disabled={!data || manuallySign.isLoading || isManualSigned}
                    title={
                      isManualSigned
                        ? "Already marked as signed manually"
                        : "Mark this consent as signed manually"
                    }
                  >
                    <CheckOutlinedIcon fontSize="small" />
                    <span>
                      {manuallySign.isLoading
                        ? "Saving..."
                        : isManualSigned
                          ? "Signed"
                          : "Mark signed"}
                    </span>
                  </button>
                </div>
              </section>

              <section
                className={cx(
                  styles.waitingRoomCard,
                  "summary-tutorial-remote-sign",
                )}
              >
                <div className={styles.waitingRoomIconBox}>
                  <LinkOutlinedIcon fontSize="small" />
                </div>

                <div className={styles.waitingRoomText}>
                  <h2 className={styles.waitingRoomTitle}>Send secure link</h2>
                  <p className={styles.waitingRoomLink} title={secureConsentLink}>
                    {secureConsentLink}
                  </p>
                </div>

                <div className={styles.waitingRoomActions}>
                  <button
                    type="button"
                    className={cx(styles.waitingRoomBtn, styles.waitingRoomBtnLight)}
                    onClick={sendReminder}
                    disabled={!data || isSendingReminder}
                  >
                    <MailOutlineOutlinedIcon fontSize="small" />
                    <span>{isSendingReminder ? "Sending..." : "Send reminder"}</span>
                  </button>

                  <button
                    type="button"
                    className={cx(styles.waitingRoomBtn, styles.waitingRoomBtnPrimary)}
                    onClick={copyRemoteLink}
                    disabled={!data}
                  >
                    <ContentCopyOutlinedIcon fontSize="small" />
                    <span>Copy link</span>
                  </button>
                </div>
              </section>

              <section
                className={cx(styles.waitingRoomCard, "summary-tutorial-qr-sign")}
              >
                <div className={styles.waitingRoomIconBox}>
                  <QrCode2OutlinedIcon fontSize="small" />
                </div>

                <div className={styles.waitingRoomText}>
                  <h2 className={styles.waitingRoomTitle}>
                    Show QR in waiting room
                  </h2>
                  <p className={styles.waitingRoomSubtitle}>
                    Patient scans to sign on their phone.
                  </p>
                </div>

                <div className={styles.waitingRoomActions}>
                  <button
                    type="button"
                    className={cx(styles.waitingRoomBtn, styles.waitingRoomBtnPrimary)}
                    onClick={() => setShowQR(true)}
                    disabled={!data}
                  >
                    <QrCode2OutlinedIcon fontSize="small" />
                    <span>Show QR</span>
                  </button>
                </div>
              </section>
            </div>

            {showQR && (
              <div
                className={styles.qrBackdrop}
                onClick={() => setShowQR(false)}
              >
                <div
                  className={styles.qrModal}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className={styles.qrWrap}>
                    <QRCode value={secureConsentLink} />
                  </div>

                  <button
                    type="button"
                    className={styles.outlineBtn}
                    onClick={() => setShowQR(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            <div className={styles.footer}>
              <button
                className={cx(
                  styles.outlineBtn,
                  "summary-tutorial-dashboard-button",
                )}
                onClick={() => navigate("/dashboard")}
              >
                Go to dashboard
              </button>
            </div>
          </>
        )}

        {activeTab === "DOCS" && (
          <>
            <section className={cx(styles.simplePanel, "summary-tutorial-docs-card")}>
              <div className={styles.panelHeader}>
                <div className={styles.panelIcon}>
                  <DescriptionOutlinedIcon fontSize="small" />
                </div>
                <div>
                  <h2 className={styles.panelTitle}>Documents &amp; downloads</h2>
                  <p className={styles.panelSubtitle}>
                    Download the latest document, older versions, or a full report.
                  </p>
                </div>
              </div>

              <div className={styles.simpleActionGrid}>
                <div className={styles.simpleActionCard}>
                  <div>
                    <span className={styles.detailLabel}>Latest document</span>
                    <h3 className={styles.simpleActionTitle}>
                      Consent PDF v{data?.version_no ?? 1}
                    </h3>
                    <p className={styles.simpleActionText}>
                      Use this for the current consent document.
                    </p>
                  </div>

                  <div className={styles.inlineActionGroup}>
                    <button
                      type="button"
                      className={cx(styles.waitingRoomBtn, styles.waitingRoomBtnLight)}
                      onClick={previewLatest}
                      disabled={!data || isDownloading || summaryQuery.isLoading}
                    >
                      <VisibilityOutlinedIcon fontSize="small" />
                      <span>Preview</span>
                    </button>

                    <button
                      type="button"
                      className={cx(styles.waitingRoomBtn, styles.waitingRoomBtnPrimary)}
                      onClick={downloadLatest}
                      disabled={!data || isDownloading || summaryQuery.isLoading}
                    >
                      <DownloadIcon fontSize="small" />
                      <span>{isDownloading ? "Preparing..." : "Download"}</span>
                    </button>
                  </div>
                </div>

                {data?.version_no && data.version_no > 1 && (
                  <div className={styles.simpleActionCard}>
                    <div>
                      <span className={styles.detailLabel}>Full report</span>
                      <h3 className={styles.simpleActionTitle}>
                        All consent versions
                      </h3>
                      <p className={styles.simpleActionText}>
                        Download the complete report when you need the full record.
                      </p>
                    </div>

                    <button
                      type="button"
                      className={cx(styles.waitingRoomBtn, styles.waitingRoomBtnPrimary)}
                      onClick={downloadFullReport}
                      disabled={!data || isDownloading}
                    >
                      <DownloadIcon fontSize="small" />
                      <span>{isDownloading ? "Downloading..." : "Download report"}</span>
                    </button>
                  </div>
                )}
              </div>

              <div className={styles.versionList}>
                {versionList && versionList.length > 1 ? (
                  versionList.map((v) => {
                    const status = versionStatusLabel(v, data?.version_no || 1);

                    return (
                      <div key={v.consent_bundle_id} className={styles.versionSimpleCard}>
                        <div>
                          <span className={styles.detailLabel}>Version</span>
                          <h3 className={styles.simpleActionTitle}>
                            v{v.version}
                          </h3>
                          <p className={styles.simpleActionText}>
                            {v.code} · {formatDate(data?.created_at)}
                          </p>
                        </div>

                        <div className={styles.versionActions}>
                          <span
                            className={cx(styles.statusTag, {
                              [styles.tagCurrent]: status === "Current",
                              [styles.tagSigned]: status === "Signed",
                              [styles.tagArchived]: status === "Archived",
                            })}
                          >
                            {status}
                          </span>

                          <button
                            type="button"
                            className={cx(styles.waitingRoomBtn, styles.waitingRoomBtnLight)}
                            onClick={() => downloadVersion(v.consent_bundle_id)}
                            disabled={isDownloading}
                          >
                            <DownloadIcon fontSize="small" />
                            <span>Download</span>
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className={styles.emptyStateCard}>
                    <strong>Only one version available</strong>
                    <span>No previous versions are available for this consent.</span>
                  </div>
                )}
              </div>
            </section>

            <section
              className={cx(
                styles.simplePanel,
                styles.complaintSimplePanel,
                "summary-tutorial-complaint",
              )}
            >
              <div className={styles.panelHeader}>
                <div className={styles.panelIconDanger}>
                  <FactCheckOutlinedIcon fontSize="small" />
                </div>
                <div>
                  <h2 className={styles.panelTitle}>Patient complaint</h2>
                  <p className={styles.panelSubtitle}>
                    Only flag this when a patient has raised a complaint linked to
                    this consent bundle.
                  </p>
                </div>

                {complaint && (
                  <span
                    className={cx(styles.complaintStatusBadge, {
                      [styles.complaintStatusPending]: complaint.status === "pending",
                      [styles.complaintStatusResolved]: complaint.status === "resolved",
                    })}
                  >
                    {String(complaint.status || "pending").toUpperCase()}
                  </span>
                )}
              </div>

              {complaintQuery.isLoading && (
                <div className={styles.emptyStateCard}>
                  <strong>Loading complaint details…</strong>
                  <span>Please wait while the complaint record is checked.</span>
                </div>
              )}

              {!complaintQuery.isLoading && complaint && (
                <div className={styles.complaintSimpleGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Issue</span>
                    <strong className={styles.detailValue}>
                      {complaint.complaint_body?.issue || "-"}
                    </strong>
                  </div>

                  <div className={cx(styles.detailItem, styles.detailWide)}>
                    <span className={styles.detailLabel}>Details</span>
                    <strong className={styles.detailValue}>
                      {complaint.complaint_body?.details || "-"}
                    </strong>
                  </div>

                  <div className={styles.inlineActionGroup}>
                    {complaint.status === "pending" && (
                      <button
                        type="button"
                        className={cx(styles.waitingRoomBtn, styles.waitingRoomBtnSuccess)}
                        onClick={() => updateComplaintStatus.mutate("resolved")}
                        disabled={updateComplaintStatus.isLoading}
                      >
                        <CheckOutlinedIcon fontSize="small" />
                        <span>
                          {updateComplaintStatus.isLoading
                            ? "Updating..."
                            : "Mark resolved"}
                        </span>
                      </button>
                    )}

                    <button
                      type="button"
                      className={cx(styles.waitingRoomBtn, styles.waitingRoomBtnLight)}
                      onClick={downloadFullReport}
                      disabled={isDownloading}
                    >
                      <DownloadIcon fontSize="small" />
                      <span>{isDownloading ? "Downloading..." : "Full report"}</span>
                    </button>
                  </div>
                </div>
              )}

              {!complaintQuery.isLoading && !complaint && (
                <div className={styles.noComplaintSimpleCard}>
                  <div>
                    <h3 className={styles.simpleActionTitle}>No complaint recorded</h3>
                    <p className={styles.simpleActionText}>
                      Keep this empty unless there is an actual complaint.
                    </p>
                  </div>

                  <button
                    type="button"
                    className={styles.flagComplaintBtn}
                    onClick={() => setShowComplaintModal(true)}
                    disabled={!data || createComplaint.isLoading}
                  >
                    Flag patient complaint
                  </button>
                </div>
              )}
            </section>

            <div className={styles.footer}>
              <button
                className={styles.outlineBtn}
                onClick={() => navigate("/dashboard")}
              >
                Go to dashboard
              </button>
            </div>
          </>
        )}

        {activeTab === "AUDIT" && (
          <>
            <section className={cx(styles.simplePanel, "summary-tutorial-audit-log")}>
              <div className={styles.panelHeader}>
                <div className={styles.panelIcon}>
                  <HistoryOutlinedIcon fontSize="small" />
                </div>
                <div>
                  <h2 className={styles.panelTitle}>Audit log</h2>
                  <p className={styles.panelSubtitle}>
                    Review what happened, when it happened, and what the patient did.
                  </p>
                </div>
              </div>

              {quickViewLoading && (
                <div className={styles.emptyStateCard}>
                  <strong>Loading audit log…</strong>
                  <span>Checking activity for this consent bundle.</span>
                </div>
              )}

              {quickViewError && (
                <p className={styles.errorText}>{quickViewError}</p>
              )}

              {!quickViewLoading &&
                !quickViewError &&
                Object.keys(interactionLog || {}).length > 0 && (
                  <div className={styles.auditTimeline}>
                    {Object.entries(interactionLog).map(
                      ([date, events]: any) => (
                        <div key={date} className={styles.auditDay}>
                          <div className={styles.auditDate}>{date}</div>

                          <div className={styles.auditEvents}>
                            {events.map((item: any, idx: number) => (
                              <div key={idx} className={styles.auditEvent}>
                                <div className={styles.auditDot} />

                                <div className={styles.auditContent}>
                                  <p className={styles.auditTitle}>
                                    {item.event}
                                  </p>

                                  {item.description?.length > 0 && (
                                    <ul className={styles.auditDesc}>
                                      {item.description.map(
                                        (d: string, i: number) => (
                                          <li key={i}>{d}</li>
                                        ),
                                      )}
                                    </ul>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                )}

              {!quickViewLoading &&
                !quickViewError &&
                Object.keys(interactionLog || {}).length === 0 && (
                  <div className={styles.emptyStateCard}>
                    <strong>No audit activity yet</strong>
                    <span>Activity will appear here after the patient opens, views, or signs the consent.</span>
                  </div>
                )}
            </section>

            <div className={styles.footer}>
              <button
                className={styles.outlineBtn}
                onClick={() => navigate("/dashboard")}
              >
                Go to dashboard
              </button>
            </div>
          </>
        )}

        <SummaryTutorialOverlay
          open={showSummaryTutorial}
          steps={summaryTutorialSteps}
          onClose={() => setShowSummaryTutorial(false)}
        />

        {/* Read-only patient note modal */}
        <Modal
          open={patientNoteOpen}
          onClose={closePatientNote}
          aria-labelledby="patient-note-modal-title"
          aria-describedby="patient-note-modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "92%", sm: "760px" },
              maxHeight: "85vh",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: { xs: 2, sm: 3 },
              borderRadius: "16px",
              outline: "none",
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
            }}
          >
            <div>
              <h2 id="patient-note-modal-title" style={{ margin: 0 }}>
                Patient note
              </h2>
              <p
                id="patient-note-modal-description"
                style={{ margin: "6px 0 16px", color: "#6b7280" }}
              >
                This note is read-only. You can view or copy it without making
                changes.
              </p>
            </div>

            <Box
              sx={{
                minHeight: "180px",
                maxHeight: "58vh",
                overflowY: "auto",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                bgcolor: "#f9fafb",
                p: { xs: 2, sm: 2.5 },
                color: "#111827",
                fontFamily: "Arial, Helvetica, sans-serif",
                fontSize: "14px",
                lineHeight: 1.55,
                "& h1, & h2, & h3, & h4, & h5, & h6": {
                  fontWeight: 700,
                  lineHeight: 1.3,
                  margin: "16px 0 8px",
                },
                "& h1": { fontSize: "24px" },
                "& h2, & h3": { fontSize: "20px" },
                "& h4, & h5, & h6": { fontSize: "16px" },
                "& p": { margin: "0 0 10px" },
                "& ul, & ol": { margin: "0 0 12px", paddingLeft: "24px" },
                "& li": { margin: "0 0 6px" },
              }}
            >
              {patientNoteLoading && <p>Loading patient note...</p>}

              {!patientNoteLoading && patientNoteError && (
                <p style={{ color: "#b42318", margin: 0 }}>
                  {patientNoteError}
                </p>
              )}

              {!patientNoteLoading && !patientNoteError && patientNote && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: patientNoteMarkdownToHtml(patientNote),
                  }}
                />
              )}
            </Box>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 18,
              }}
            >
              <Button variant="outlined" color="inherit" onClick={closePatientNote}>
                Close
              </Button>

              
            </div>
          </Box>
        </Modal>

        {/* Complaint modal */}
        <Modal
          open={showComplaintModal}
          onClose={handleCloseComplaintModal}
          aria-labelledby="complaint-modal-title"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "92%", sm: "680px" },
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 3,
              borderRadius: "16px",
              outline: "none",
            }}
          >
            <h2 id="complaint-modal-title" style={{ marginTop: 0 }}>
              Flag Patient Complaint
            </h2>

            <p className={styles.mutedSmall}>
              Add the complaint issue and details. This will create a complaint
              record against consent bundle ID {consentBundleId}.
            </p>

            <TextField
              label="Complaint issue"
              value={complaintIssue}
              onChange={(e) => setComplaintIssue(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="Wrong treatment provided"
            />

            <TextField
              label="Complaint details"
              value={complaintDetails}
              onChange={(e) => setComplaintDetails(e.target.value)}
              fullWidth
              multiline
              minRows={4}
              margin="normal"
              placeholder="Tooth extraction was incorrect"
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 18,
              }}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCloseComplaintModal}
                disabled={createComplaint.isLoading}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={handleCreateComplaint}
                disabled={createComplaint.isLoading}
              >
                {createComplaint.isLoading ? "Saving..." : "Flag Complaint"}
              </Button>
            </div>
          </Box>
        </Modal>

        {/* Clinician signature modal (MUI) */}
        <Modal
          open={isModalOpen}
          onClose={handleModalClose}
          aria-labelledby="modal-signature"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "92%", sm: "640px" },
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 3,
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            <h2 id="modal-signature" style={{ marginTop: 0 }}>
              Sign Consent
            </h2>

            {/* selector (desktop only, matches old behavior) */}
            {!isMobile && (
              <div style={{ marginBottom: 12 }}>
                <Chip
                  label="Signature"
                  variant={activeInput === "signature" ? "filled" : "outlined"}
                  onClick={() => {
                    setActiveInput("signature");
                    setIsSignatureCaptured(false);
                  }}
                  sx={{ mr: 2 }}
                />
                <Chip
                  label="Type Name"
                  variant={activeInput === "name" ? "filled" : "outlined"}
                  onClick={() => setActiveInput("name")}
                />
              </div>
            )}

            {activeInput === "signature" && (
              <div
                style={{
                  width: "100%",
                  border: "2px solid #243474",
                  margin: "10px 0",
                  padding: "10px",
                  borderRadius: "10px",
                  boxSizing: "border-box",
                  overflow: "hidden",
                }}
              >
                <SignatureCanvas
                  penColor="#243474"
                  ref={mySignature}
                  canvasProps={{
                    width: isMobile
                      ? Math.min(window.innerWidth * 0.85, 420)
                      : 600,
                    height: isMobile ? 220 : 320,
                    style: { width: "100%", height: "100%" },
                  }}
                  onBegin={() => setIsSignatureCaptured(true)}
                />
              </div>
            )}

            {activeInput === "name" && (
              <TextField
                label="Full Name"
                variant="outlined"
                fullWidth
                value={userFullName}
                onChange={(e) => setUserFullName(e.target.value)}
                sx={{ mt: 1 }}
              />
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 12,
              }}
            >
              <Button
                variant="outlined"
                color="error"
                onClick={handleModalClose}
              >
                Close
              </Button>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {activeInput === "signature" && (
                  <>
                    <IconButton aria-label="erase" onClick={eraseSignature}>
                      <DeleteIcon />
                    </IconButton>

                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleSignatureSave}
                      disabled={!isSignatureCaptured || signLegacy.isLoading}
                    >
                      Save Signature
                    </Button>
                  </>
                )}

                {activeInput === "name" && (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleNameSave}
                    disabled={!userFullName || signLegacy.isLoading}
                  >
                    Save Name
                  </Button>
                )}
              </div>
            </div>
          </Box>
        </Modal>
      </div>
    </>
  );
}

/* -------------------------- Local helper (name) -------------------------- */
function getPdfName(s: SummaryDTO | any) {
  const patient = s?.patient;
  const name = patient
    ? `${patient.first_name || ""}_${patient.last_name || ""}`.replace(
        /\s+/g,
        "_",
      )
    : "patient";
  const ver = s?.version_no ? `v${s.version_no}` : "v1";
  return `consent_${name}_${ver}.pdf`;
}
